/**
 * Comparative Analysis Section for Accountant Report View
 * Displays industry benchmarks, similar claims, and data quality indicators
 */

import React from 'react';
import CommentsSection from '../CommentsSection';

interface ComparativeAnalysis {
  status?: string;
  client_loss?: number;
  event_type?: string;
  comparisons?: {
    sample_size?: number;
    similar_claims_average?: number;
    similar_claims_median?: number;
  };
  percentile?: {
    value?: number;
    interpretation?: string;
  };
  insights?: any[];
  comparable_claims?: Array<{
    business_type: string;
    event_type: string;
    loss_amount: number;
  }>;
  loss_to_revenue_ratio?: number;
  business_revenue_estimate?: number;
  data_quality?: {
    reliability?: 'HIGH' | 'MEDIUM' | 'LOW';
    sample_size?: number;
  };
  error?: string;
}

interface ComparativeAnalysisSectionProps {
  reportId: string;
  businessType?: string;
  comparativeAnalysis: ComparativeAnalysis | null;
  loadingAnalysis: boolean;
  canComment: boolean;
  getAuthHeader: () => { Authorization: string };
  formatCurrency: (amount?: number) => string;
}

export default function ComparativeAnalysisSection({
  reportId,
  businessType,
  comparativeAnalysis,
  loadingAnalysis,
  canComment,
  getAuthHeader,
  formatCurrency
}: ComparativeAnalysisSectionProps) {
  const getReliabilityColors = (reliability?: string) => {
    switch (reliability) {
      case 'HIGH':
        return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900', subtext: 'text-green-800', icon: 'text-green-600' };
      case 'MEDIUM':
        return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900', subtext: 'text-yellow-800', icon: 'text-yellow-600' };
      default:
        return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-900', subtext: 'text-orange-800', icon: 'text-orange-600' };
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <span className="text-2xl mr-2">📊</span>
        Comparative Analysis - Industry Context
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        This section compares the claim against industry benchmarks and similar events to validate reasonableness. 
        Critical for establishing credibility in litigation and demonstrating the claim is not an outlier.
      </p>
      
      {loadingAnalysis ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-600 mt-2">Loading comparative analysis...</p>
        </div>
      ) : comparativeAnalysis && comparativeAnalysis.status === 'success' ? (
        <>
          {/* Claim vs Industry Benchmarks */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border-l-4 border-green-600">
              <p className="text-xs text-green-700 font-medium mb-1">THIS CLAIM</p>
              <p className="text-sm text-green-800 mb-2 font-semibold">Final Amount</p>
              <p className="text-3xl font-bold text-green-900 mb-2">
                {formatCurrency(comparativeAnalysis.client_loss)}
              </p>
              <div className="text-xs text-green-700 bg-green-200 rounded px-2 py-1">
                {businessType || 'Business'} - {comparativeAnalysis.event_type}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-400">
              <p className="text-xs text-gray-600 font-medium mb-1">SIMILAR CLAIMS AVERAGE</p>
              <p className="text-sm text-gray-700 mb-2 font-semibold">
                {comparativeAnalysis.comparisons?.sample_size || 0} Similar Cases
              </p>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                {formatCurrency(comparativeAnalysis.comparisons?.similar_claims_average || 0)}
              </p>
              <div className="text-xs text-gray-700 bg-gray-200 rounded px-2 py-1">
                Median: {formatCurrency(comparativeAnalysis.comparisons?.similar_claims_median || 0)}
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
              <p className="text-xs text-blue-700 font-medium mb-1">PERCENTILE RANK</p>
              <p className="text-sm text-blue-800 mb-2 font-semibold">Comparison</p>
              <p className="text-3xl font-bold text-blue-900 mb-2">
                {comparativeAnalysis.percentile?.value?.toFixed(0) || 0}th
              </p>
              <div className="text-xs text-blue-700 bg-blue-100 rounded px-2 py-1">
                {comparativeAnalysis.percentile?.interpretation || 'Average'}
              </div>
            </div>
          </div>

          {/* Insights from AI */}
          {comparativeAnalysis.insights && comparativeAnalysis.insights.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-purple-900 mb-3 flex items-center">
                <span className="mr-2">💡</span>
                Key Insights
              </h3>
              <ul className="space-y-2">
                {comparativeAnalysis.insights.map((insight: any, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-purple-900">
                    <span className="text-purple-600 mt-0.5">•</span>
                    <span>
                      {typeof insight === 'string' ? insight : insight.message || insight.title || JSON.stringify(insight)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Comparable Claims */}
          {comparativeAnalysis.comparable_claims && comparativeAnalysis.comparable_claims.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                <span className="mr-2">📋</span>
                Similar Cases in Database ({comparativeAnalysis.comparable_claims.length})
              </h3>
              <div className="space-y-2">
                {comparativeAnalysis.comparable_claims.map((claim, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                    <span className="text-blue-900">
                      {claim.business_type} - {claim.event_type}
                    </span>
                    <span className="font-semibold text-blue-700">
                      {formatCurrency(claim.loss_amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loss to Revenue Ratio */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-indigo-900 mb-2">Loss to Revenue Ratio</h3>
            <p className="text-sm text-indigo-800">
              This claim represents <span className="font-bold">{comparativeAnalysis.loss_to_revenue_ratio?.toFixed(1)}%</span> of 
              estimated annual revenue ({formatCurrency(comparativeAnalysis.business_revenue_estimate || 0)})
            </p>
          </div>

          {/* Data Quality Indicator */}
          {comparativeAnalysis.data_quality && (
            <div className={`border rounded-lg p-4 ${getReliabilityColors(comparativeAnalysis.data_quality.reliability).bg} ${getReliabilityColors(comparativeAnalysis.data_quality.reliability).border}`}>
              <div className="flex items-start gap-3">
                <svg className={`w-5 h-5 mt-0.5 ${getReliabilityColors(comparativeAnalysis.data_quality.reliability).icon}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className={`text-sm font-semibold mb-1 ${getReliabilityColors(comparativeAnalysis.data_quality.reliability).text}`}>
                    {comparativeAnalysis.data_quality.reliability} Reliability Analysis
                  </p>
                  <p className={`text-sm ${getReliabilityColors(comparativeAnalysis.data_quality.reliability).subtext}`}>
                    Based on {comparativeAnalysis.data_quality.sample_size} similar claims in your database. 
                    {comparativeAnalysis.data_quality.reliability === 'HIGH' && ' Strong statistical confidence in benchmarks.'}
                    {comparativeAnalysis.data_quality.reliability === 'MEDIUM' && ' Moderate statistical confidence. Consider adding more comparative data.'}
                    {comparativeAnalysis.data_quality.reliability === 'LOW' && ' Limited comparative data. Benchmarks should be used with caution.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <p className="text-sm text-yellow-800">
            {comparativeAnalysis?.error ? `Analysis error: ${comparativeAnalysis.error}` : 'No comparative analysis data available'}
          </p>
        </div>
      )}
      
      {/* Comments on Comparative Analysis */}
      {canComment && (
        <CommentsSection
          reportId={reportId}
          section="comparative_analysis"
          sectionTitle="Comparative Analysis"
          getAuthHeader={getAuthHeader}
          canComment={true}
        />
      )}
    </div>
  );
}
